/**
 * Web Audio FX graph for the site player.
 * Loudness / headroom patterns adapted from Cadence (MIT).
 * Late Night = Apple Music–style dynamic-range reduction after EQ.
 *
 * Chain: source → loudness (RG) → EQ headroom → EQ → late night → soft ceiling → out
 */

import {
  EQ_DEFAULT_Q,
  EQ_FREQUENCIES,
  eqBandType,
  eqHeadroomDb,
} from '@/lib/dsp';
import type { Track } from '@/types';

export type AudioFxSettings = {
  loudnessEnabled: boolean;
  /** Dynamic-range reduction for quiet listening (Apple Music “Late Night”). */
  lateNightEnabled: boolean;
  eqGains: number[];
};

type Graph = {
  ctx: AudioContext;
  source: MediaElementAudioSourceNode;
  loudness: GainNode;
  headroom: GainNode;
  filters: BiquadFilterNode[];
  lateNight: DynamicsCompressorNode;
  lateNightDry: GainNode;
  lateNightWet: GainNode;
  lateNightMakeup: GainNode;
  ceiling: WaveShaperNode;
  output: GainNode;
};

let graph: Graph | null = null;
let unavailable = false;

/** Late Night makeup so average level stays useful after heavy compression. */
const LATE_NIGHT_MAKEUP_DB = 6;

/** Identity curve — WaveShaper must stay in the graph (can't disconnect live). */
const CEILING_BYPASS = (() => {
  const curve = new Float32Array(2);
  curve[0] = -1;
  curve[1] = 1;
  return curve;
})();

function rampGain(ctx: AudioContext, node: GainNode, target: number, glide = 0.05) {
  const now = ctx.currentTime;
  node.gain.setTargetAtTime(Math.max(0, target), now, glide);
}

function makeCeilingCurve(ceilingDb = -1) {
  const n = 2048;
  const curve = new Float32Array(n);
  const ceiling = Math.pow(10, ceilingDb / 20);
  const kneeDb = 2;
  const kneeStart = ceiling * Math.pow(10, -kneeDb / 20);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const ax = Math.abs(x);
    let y: number;
    if (ax <= kneeStart) {
      y = ax;
    } else {
      const t = (ax - kneeStart) / (ceiling - kneeStart || 1e-6);
      const shaped = ceiling - (ceiling - kneeStart) * Math.exp(-3 * t);
      y = Math.min(ceiling, shaped);
    }
    curve[i] = Math.sign(x) * y;
  }
  return curve;
}

export function isAudioFxUnavailable() {
  return unavailable;
}

/**
 * iOS/iPadOS suspends AudioContext on lock screen, which silences any element
 * claimed by createMediaElementSource. Prefer native HTMLAudioElement there.
 */
export function preferNativePlaybackAudio(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS 13+ may report as Mac with touch
  if (
    (navigator.platform === 'MacIntel' || /Macintosh/.test(ua)) &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

/** True once createMediaElementSource has claimed an element. */
export function hasAudioFxGraph() {
  return Boolean(graph);
}

export function ensureAudioFx(audio: HTMLAudioElement): Graph | null {
  if (unavailable) return null;
  // Never capture the element on iOS — lock screen would kill on-demand audio.
  if (preferNativePlaybackAudio()) return null;
  if (graph) return graph;
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new AudioCtx({ latencyHint: 'playback' });
    const source = ctx.createMediaElementSource(audio);

    const loudness = ctx.createGain();
    const headroom = ctx.createGain();
    const filters = EQ_FREQUENCIES.map((freq, i) => {
      const f = ctx.createBiquadFilter();
      f.type = eqBandType(i);
      f.frequency.value = freq;
      f.Q.value = EQ_DEFAULT_Q;
      f.gain.value = 0;
      return f;
    });

    // Apple Music–like Late Night: raise quiet material, tame peaks
    const lateNight = ctx.createDynamicsCompressor();
    lateNight.threshold.value = -28;
    lateNight.knee.value = 14;
    lateNight.ratio.value = 6;
    lateNight.attack.value = 0.02;
    lateNight.release.value = 0.35;

    const lateNightDry = ctx.createGain();
    const lateNightWet = ctx.createGain();
    const lateNightMakeup = ctx.createGain();
    lateNightDry.gain.value = 1;
    lateNightWet.gain.value = 0;
    lateNightMakeup.gain.value = Math.pow(10, LATE_NIGHT_MAKEUP_DB / 20);

    const ceiling = ctx.createWaveShaper();
    // Start as passthrough; applyAudioFxSettings enables shaping only when needed.
    (ceiling as WaveShaperNode & { curve: Float32Array | null }).curve =
      CEILING_BYPASS;
    ceiling.oversample = '2x';

    const output = ctx.createGain();
    output.gain.value = 1;

    // loudness (Sound Check) → EQ headroom → EQ → Late Night → soft ceiling
    let node: AudioNode = source;
    node.connect(loudness);
    node = loudness;
    node.connect(headroom);
    node = headroom;
    for (const f of filters) {
      node.connect(f);
      node = f;
    }

    node.connect(lateNightDry);
    node.connect(lateNight);
    lateNight.connect(lateNightMakeup);
    lateNightMakeup.connect(lateNightWet);
    lateNightDry.connect(ceiling);
    lateNightWet.connect(ceiling);
    ceiling.connect(output);
    output.connect(ctx.destination);

    graph = {
      ctx,
      source,
      loudness,
      headroom,
      filters,
      lateNight,
      lateNightDry,
      lateNightWet,
      lateNightMakeup,
      ceiling,
      output,
    };
    return graph;
  } catch (err) {
    console.warn('Audio FX unavailable (CORS or Web Audio)', err);
    unavailable = true;
    graph = null;
    return null;
  }
}

export async function resumeAudioFx() {
  if (!graph) return;
  if (graph.ctx.state === 'suspended') {
    try {
      await graph.ctx.resume();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Track ReplayGain in dB (Apple Music Sound Check / −18 LUFS).
 * Cap at 0 dB: without true-peak metadata, boosts clip into the soft ceiling
 * and sound like static/distortion on already-hot Bandcamp masters.
 */
export function replayGainTrackDb(track: Track | null | undefined): number {
  const raw = track?.replayGainTrack ?? track?.replayGain;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0;
  return Math.max(-15, Math.min(0, raw));
}

/**
 * ReplayGain when tagged + Normalize on.
 * Loudness and EQ are independent stages — both apply when enabled.
 */
function loudnessDbFromTrack(
  track: Track | null | undefined,
  settings: AudioFxSettings,
): number {
  if (!settings.loudnessEnabled) return 0;
  return replayGainTrackDb(track);
}

/**
 * Apply ReplayGain via element.volume (native path — iOS lock-screen safe).
 * @param force when true (live radio), always normalize; otherwise honor `enabled`.
 */
export function applyNativeElementLoudness(
  audio: HTMLAudioElement,
  track: Track | null | undefined,
  enabled: boolean,
  opts?: { force?: boolean; untaggedDb?: number },
): void {
  const on = opts?.force || enabled;
  if (!on) {
    try {
      audio.volume = 1;
    } catch {
      /* ignore */
    }
    return;
  }
  const tagged = replayGainTrackDb(track);
  const untagged = opts?.untaggedDb ?? 0;
  const useDb = tagged !== 0 ? tagged : untagged;
  const linear = Math.pow(10, useDb / 20);
  try {
    audio.volume = Math.min(1, Math.max(0.08, linear));
  } catch {
    /* ignore */
  }
}

/** Live radio always levels (ignores Normalize toggle). */
export function applyRadioElementLoudness(
  audio: HTMLAudioElement,
  track: Track | null | undefined,
): void {
  // Untagged: mild broadcast headroom so hot masters don't jump.
  applyNativeElementLoudness(audio, track, true, { force: true, untaggedDb: -1.5 });
}

/** Restore unity gain when leaving live / turning Normalize off. */
export function clearRadioElementLoudness(audio: HTMLAudioElement | null | undefined): void {
  if (!audio) return;
  try {
    audio.volume = 1;
  } catch {
    /* ignore */
  }
}

export function applyAudioFxSettings(
  settings: AudioFxSettings,
  track?: Track | null,
) {
  if (!graph) return;
  const { ctx } = graph;
  const sampleRate = ctx.sampleRate || 44100;

  // 1) Loudness / Sound Check (attenuate only — see replayGainTrackDb)
  const loudDb = loudnessDbFromTrack(track, settings);
  rampGain(ctx, graph.loudness, Math.pow(10, loudDb / 20), 0.05);

  // 2) Always apply pre-EQ headroom so boosts stay clean with Normalize / Late Night
  const trimDb = eqHeadroomDb(settings.eqGains, sampleRate);
  rampGain(ctx, graph.headroom, Math.pow(10, trimDb / 20), 0.05);

  // 3) Graphic EQ
  settings.eqGains.forEach((g, i) => {
    const filter = graph!.filters[i];
    if (!filter) return;
    filter.gain.setTargetAtTime(
      typeof g === 'number' && Number.isFinite(g) ? g : 0,
      ctx.currentTime,
      0.08,
    );
  });

  // 4) Late Night after EQ — dry bypass vs compressed+makeup wet
  const lateNightOn = settings.lateNightEnabled;
  graph.lateNightWet.gain.setTargetAtTime(lateNightOn ? 1 : 0, ctx.currentTime, 0.04);
  graph.lateNightDry.gain.setTargetAtTime(lateNightOn ? 0 : 1, ctx.currentTime, 0.04);

  // 5) Soft ceiling only when something can push above full scale.
  // Hot Bandcamp masters through a −1 dB waveshaper sound like static/crackle.
  const needsCeiling = lateNightOn || trimDb < 0;
  const curve = needsCeiling ? makeCeilingCurve(-1) : CEILING_BYPASS;
  try {
    (graph.ceiling as WaveShaperNode & { curve: Float32Array | null }).curve =
      curve;
  } catch {
    /* some engines reject curve swaps mid-render */
  }
}

export function getAudioFxSampleRate(): number {
  return graph?.ctx.sampleRate ?? 44100;
}
